import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, MapPin, Pencil, Plus, Trash2, XCircle } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { MAP_PROVIDER_GOOGLE_CODE, MAP_PROVIDER_OPENSTREETMAP_CODE } from '@/constants/map-providers'
import { useGoogleMapsApiKey } from '@/hooks/use-google-maps-api-key'
import { getErrorMessage } from '@/lib/api'
import { getServiceInstanceIdFromSearch } from '@/lib/service-instance'
import { isOrganizationServiceVariant } from '@/lib/service-variant-types'
import { orgTreeApi, type OrgTreeAddressPayload, type OrgTreeNodeAddress } from '@/services/org-tree'
import {
  setOptions,
  importLibrary,
} from '@googlemaps/js-api-loader'

type LocationSearchOption = {
  id: string
  placeId: string
  label: string
  lat: string
  lon: string
  doorNo: string
  street: string
  city: string
  zipcode: string
}

type AddressFormState = {
  addressFor: string
  fname: string
  lname: string
  doorNo: string
  building: string
  street: string
  postcode: string
  country: string
  state: string
  cityName: string
  latitude: string
  longitude: string
}

const createDefaultFormState = (): AddressFormState => ({
  addressFor: '',
  fname: '',
  lname: '',
  doorNo: '',
  building: '',
  street: '',
  postcode: '',
  country: '',
  state: '',
  cityName: '',
  latitude: '',
  longitude: '',
})

const toInputValue = (value?: string | number | null) => (value == null ? '' : String(value))

const resolveCountryId = (address: OrgTreeNodeAddress | null | undefined) => toInputValue(address?.country)

const resolveStateId = (address: OrgTreeNodeAddress | null | undefined) => toInputValue(address?.state)

const createFormState = (address?: OrgTreeNodeAddress | null): AddressFormState => ({
  addressFor: toInputValue(address?.address_for ?? address?.name),
  fname: toInputValue(address?.fname),
  lname: toInputValue(address?.lname),
  doorNo: toInputValue(address?.door_no),
  building: toInputValue(address?.fline),
  street: toInputValue(address?.sline),
  postcode: toInputValue(address?.zipcode),
  country: resolveCountryId(address),
  state: resolveStateId(address),
  cityName: toInputValue(address?.city_name),
  latitude: toInputValue(address?.latitude),
  longitude: toInputValue(address?.longitude),
})

type NodeAddressManagerProps = {
  nodeId: string | number
  title?: string
  description?: string
}

export default function NodeAddressManager({
  nodeId,
  title = 'Address',
  description = 'The primary address recorded for this node.',
}: NodeAddressManagerProps) {
  const location = useLocation()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const { mapsApiKey } = useGoogleMapsApiKey({ serviceInstanceReference: serviceInstanceId })
  const queryClient = useQueryClient()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [addressForm, setAddressForm] = useState<AddressFormState>(createDefaultFormState)
  const [editingAddress, setEditingAddress] = useState<OrgTreeNodeAddress | null>(null)
  const [hasResolvedEditState, setHasResolvedEditState] = useState(false)
  const [locationSearch, setLocationSearch] = useState('')
  const [locationSearchOptions, setLocationSearchOptions] = useState<LocationSearchOption[]>([])
  const [isSearchingLocation, setIsSearchingLocation] = useState(false)
  const [searchFallbackUsed, setSearchFallbackUsed] = useState(false)
  const [lastResolvedProvider] =
  useState<'googlemaps' | 'openstreetmap'>('openstreetmap')
  const [lastProviderSource, setLastProviderSource] = useState<'org-instance' | 'service-instance'>('service-instance')

  const queryKey = ['org-tree-addresses', nodeId]

  const addressesQuery = useQuery({
    queryKey,
    queryFn: () => orgTreeApi.listAddresses(nodeId),
    enabled: Boolean(nodeId),
  })

  const countriesQuery = useQuery({
    queryKey: ['org-tree-countries'],
    queryFn: orgTreeApi.listCountries,
    staleTime: 60_000,
  })

  const statesQuery = useQuery({
    queryKey: ['org-tree-states', addressForm.country],
    queryFn: () => orgTreeApi.listStates(addressForm.country),
    enabled: Boolean(addressForm.country),
    staleTime: 60_000,
  })

  const serviceInstanceQuery = useQuery({
    queryKey: ['org-tree-service-instance', serviceInstanceId],
    queryFn: () => orgTreeApi.getServiceInstance(serviceInstanceId as string),
    enabled: Boolean(serviceInstanceId),
    staleTime: 60_000,
  })

  const isOrganizationInstanceContext = isOrganizationServiceVariant(
    serviceInstanceQuery.data?.service_data?.variant_unique ?? serviceInstanceQuery.data?.service_variant,
    serviceInstanceQuery.data?.service_variant_name,
  )

  const providerSourceLabel =
    lastProviderSource === 'org-instance' || isOrganizationInstanceContext ? 'Organization' : 'Service Branch'

  const address = useMemo(() => addressesQuery.data?.[0] ?? null, [addressesQuery.data])

  useEffect(() => {
    if (!editingAddress || hasResolvedEditState || countriesQuery.data == null) {
      return
    }

    const countryId = resolveCountryId(editingAddress)
      || String(countriesQuery.data.find((country) => country.country_name === editingAddress.country_name)?.id ?? '')

    if (countryId && countryId !== addressForm.country) {
      setAddressForm((current) => ({ ...current, country: countryId }))
      return
    }

    setHasResolvedEditState(true)
  }, [addressForm.country, countriesQuery.data, editingAddress, hasResolvedEditState])

  useEffect(() => {
    if (!editingAddress || !addressForm.country || hasResolvedEditState === false || statesQuery.data == null) {
      return
    }

    if (addressForm.state) {
      return
    }

    const stateId = resolveStateId(editingAddress)
      || String(statesQuery.data.find((state) => state.name === editingAddress.state_name)?.id ?? '')

    if (stateId) {
      setAddressForm((current) => ({ ...current, state: stateId }))
    }
  }, [addressForm.country, addressForm.state, editingAddress, hasResolvedEditState, statesQuery.data])

useEffect(() => {
  if (!isDialogOpen) return

  const query = locationSearch.trim()

  if (query.length < 3) {
    setLocationSearchOptions([])
    return
  }

  const timeoutId = setTimeout(async () => {
    try {
      setIsSearchingLocation(true)

      if (!mapsApiKey) {
        setLocationSearchOptions([])
        return
      }

      setOptions({
        key: mapsApiKey,
        v: 'weekly',
      })

      await importLibrary('places')

      const service =
        new google.maps.places.AutocompleteService()

      service.getPlacePredictions(
        {
          input: query,
          types: ['address'],
          componentRestrictions: {
            country: 'in',
          },
        },
        (predictions, status) => {
          if (
            status !==
              google.maps.places
                .PlacesServiceStatus.OK ||
            !predictions
          ) {
            setLocationSearchOptions([])
            return
          }

          setLocationSearchOptions(
            predictions.map((place) => ({
              id: place.place_id ?? '',
              placeId:
                place.place_id ?? '',
              label:
                place.description ?? '',
              lat: '',
              lon: '',
              doorNo: '',
              street: '',
              city: '',
              zipcode: '',
            }))
          )
        }
      )
    } catch (error) {
      console.error(
        'Google search error',
        error
      )
      setLocationSearchOptions([])
    } finally {
      setIsSearchingLocation(false)
    }
  }, 350)

  return () => clearTimeout(timeoutId)
}, [isDialogOpen, locationSearch, mapsApiKey])

  const resetDialog = () => {
    setIsDialogOpen(false)
    setEditingAddress(null)
    setAddressForm(createDefaultFormState())
    setHasResolvedEditState(false)
    setLocationSearch('')
    setLocationSearchOptions([])
    setIsSearchingLocation(false)
    setSearchFallbackUsed(false)
    setLastProviderSource('service-instance')
  }

  const invalidateAddresses = async () => {
    await queryClient.invalidateQueries({ queryKey })
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const normalizedAddressFor = addressForm.addressFor.trim()
      const normalizedDoorNo = addressForm.doorNo.trim()
      const normalizedPostcode = addressForm.postcode.trim()

      if (!normalizedAddressFor) {
        throw new Error('Address For is required')
      }

      if (!normalizedDoorNo) {
        throw new Error('Door No is required')
      }

      if (!normalizedPostcode) {
        throw new Error('Postcode is required')
      }

      if ((addressForm.latitude && !addressForm.longitude) || (!addressForm.latitude && addressForm.longitude)) {
        throw new Error('Latitude and longitude must be provided together')
      }

      const payload: OrgTreeAddressPayload = {
        node: nodeId,
        name: normalizedAddressFor,
        address_for: normalizedAddressFor,
        fname: addressForm.fname,
        lname: addressForm.lname,
        door_no: normalizedDoorNo,
        fline: addressForm.building,
        sline: addressForm.street,
        zipcode: normalizedPostcode,
        country: addressForm.country || null,
        state: addressForm.state || null,
        city_name: addressForm.cityName,
        latitude: addressForm.latitude || null,
        longitude: addressForm.longitude || null,
      }

      if (editingAddress) {
        return orgTreeApi.updateAddress(nodeId, payload)
      }

      return orgTreeApi.createAddress(nodeId, payload)
    },
    onSuccess: async () => {
      await invalidateAddresses()
      resetDialog()
      toast.success(editingAddress ? 'Address updated successfully' : 'Address added successfully')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => orgTreeApi.deleteAddress(nodeId),
    onSuccess: async () => {
      await invalidateAddresses()
      toast.success('Address deleted successfully')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

     const toggleActiveMutation = useMutation({
       mutationFn: (newActive: number) => orgTreeApi.toggleAddressActive(nodeId, newActive),
       onSuccess: async () => {
         await invalidateAddresses()
         toast.success(address?.active === 1 ? 'Address deactivated' : 'Address activated')
       },
       onError: (error) => {
         toast.error(getErrorMessage(error))
       },
     })

  const openCreate = () => {
    setEditingAddress(null)
    setAddressForm(createDefaultFormState())
    setHasResolvedEditState(true)
    setIsDialogOpen(true)
  }

  const openEdit = () => {
    if (!address) {
      return
    }

    setEditingAddress(address)
    setAddressForm(createFormState(address))
    setHasResolvedEditState(false)
    setIsDialogOpen(true)
  }


     const isActive = address?.active !== 0
     const hasCoordinates = Boolean(address?.latitude && address?.longitude)
     const addressLine1 = [address?.door_no, address?.fline].filter(Boolean).join(', ')
     const addressLine2 = address?.sline
     const addressCityLine = [address?.city_name, address?.state_name].filter(Boolean).join(', ')
     const addressPostLine = [addressCityLine, address?.zipcode].filter(Boolean).join(' — ')

     return (
    <>
      <Card className="overflow-hidden rounded-[24px] border-border/80 bg-gradient-to-b from-background to-muted/10 shadow-sm">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <MapPin className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-xl">{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">Primary Address</Badge>
              <Badge variant={hasCoordinates ? 'default' : 'outline'}>{hasCoordinates ? 'Map Ready' : 'Coordinates Missing'}</Badge>
               {address ? (
                 isActive
                   ? <Badge variant="default" className="gap-1"><CheckCircle2 className="h-3 w-3" />Active</Badge>
                   : <Badge variant="outline" className="gap-1 text-muted-foreground"><XCircle className="h-3 w-3" />Inactive</Badge>
               ) : (
                 <Badge variant="outline">Not Set</Badge>
               )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 sm:justify-end">
            {address ? (
              <>
                <Button variant="outline" className="bg-background/80" onClick={openEdit}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit Address
                </Button>
                 <Button
                   variant="outline"
                   className={isActive ? 'text-amber-600 hover:bg-amber-50 hover:text-amber-700 border-amber-200' : 'text-green-600 hover:bg-green-50 hover:text-green-700 border-green-200'}
                   onClick={() => toggleActiveMutation.mutate(isActive ? 0 : 1)}
                   disabled={toggleActiveMutation.isPending}
                 >
                   {isActive ? <XCircle className="mr-2 h-4 w-4" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                   {toggleActiveMutation.isPending ? 'Updating...' : isActive ? 'Deactivate' : 'Activate'}
                 </Button>
                <Button variant="ghost" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </>
            ) : (
              <Button className="shadow-sm" onClick={openCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Add Address
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {addressesQuery.isLoading ? (
            <div className="rounded-3xl border border-dashed border-border/70 bg-muted/20 p-10 text-sm text-muted-foreground">
              Loading address...
            </div>
          ) : address ? (
            <>
               {/* Address card */}
               <div className={`relative overflow-hidden rounded-3xl border bg-gradient-to-br p-6 shadow-sm transition-opacity ${isActive ? 'border-border/70 from-background to-muted/20 opacity-100' : 'border-border/40 from-muted/30 to-muted/10 opacity-70'}`}>
                 {!isActive && (
                   <div className="absolute inset-0 flex items-center justify-center rounded-3xl bg-background/50 backdrop-blur-[1px]">
                     <div className="flex items-center gap-2 rounded-xl border border-border/60 bg-background/90 px-4 py-2 shadow-sm">
                       <XCircle className="h-4 w-4 text-muted-foreground" />
                       <span className="text-sm font-medium text-muted-foreground">Address inactive</span>
                     </div>
                   </div>
                 )}
                 <div className="flex items-start justify-between gap-4">
                   <div className="flex items-center gap-3">
                     <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                       <MapPin className="h-5 w-5" />
                     </div>
                     <div>
                       <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Address For</p>
                       <p className="mt-0.5 font-semibold text-foreground">{address?.address_for ?? address?.name ?? '—'}</p>
                     </div>
                   </div>
                   <div className="flex flex-wrap gap-2">
                     {hasCoordinates ? <Badge variant="default" className="text-xs">GPS Ready</Badge> : <Badge variant="outline" className="text-xs">No GPS</Badge>}
                     {address?.zipcode ? <Badge variant="secondary" className="text-xs">{address.zipcode}</Badge> : null}
                   </div>
                 </div>
                 <div className="mt-5 space-y-1.5 font-mono text-sm text-foreground">
                   {address?.fname || address?.lname ? (
                     <p className="font-sans font-medium text-foreground/90">
                       {[address?.fname, address?.lname].filter(Boolean).join(' ')}
                     </p>
                   ) : null}
                   {addressLine1 ? <p>{addressLine1}</p> : null}
                   {addressLine2 ? <p>{addressLine2}</p> : null}
                   {addressPostLine ? <p>{addressPostLine}</p> : null}
                   {address?.country_name ? <p className="font-sans text-xs font-medium uppercase tracking-widest text-muted-foreground">{address.country_name}</p> : null}
                 </div>
                 {hasCoordinates ? (
                   <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                     <MapPin className="h-3 w-3" />
                     <span>{address?.latitude}, {address?.longitude}</span>
                   </div>
                 ) : null}
               </div>
            </>
          ) : (
            <div className="rounded-3xl border border-dashed border-border/70 bg-linear-to-br from-primary/5 via-background to-muted/20 p-10 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <MapPin className="h-7 w-7" />
              </div>
              <p className="font-medium text-foreground">No address configured</p>
              <p className="mt-2 text-sm text-muted-foreground">Add the primary address for this node to make location details easier to review.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        if (!open) {
          resetDialog()
          return
        }

        setIsDialogOpen(true)
      }}>
        <DialogContent className="flex max-h-[76vh] flex-col overflow-hidden p-0 sm:max-w-lg">
          <DialogHeader className="border-b px-5 py-2.5">
            <DialogTitle>{editingAddress ? 'Edit Address' : 'Add Address'}</DialogTitle>
            <DialogDescription>Manage the primary address linked to this node.</DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto px-5 py-2.5">
            <div className="rounded-2xl border border-border/70 bg-muted/15 p-2.5">
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Search Location</p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <div className="grid gap-2 sm:col-span-2">
              <Label htmlFor="node_location_search">Search Location</Label>
              <Input
                id="node_location_search"
                value={locationSearch}
                onChange={(event) => setLocationSearch(event.target.value)}
                placeholder="Search by address or place"
              />
              <p className="text-xs text-muted-foreground">Pick a result to auto-fill Door No, Street, City, Postcode, and coordinates.</p>
              {!serviceInstanceId ? (
                <p className="text-xs text-amber-600">Organization context is missing; location search is unavailable.</p>
              ) : null}
              {searchFallbackUsed ? (
                <p className="text-xs text-amber-600">Google search fallback triggered. Showing OpenStreetMap results from {providerSourceLabel} settings.</p>
              ) : null}
              {isSearchingLocation ? (
                <p className="text-xs text-muted-foreground">Searching locations...</p>
              ) : null}
              {!isSearchingLocation && locationSearch.trim().length >= 3 && locationSearchOptions.length === 0 ? (
                <p className="text-xs text-muted-foreground">No matching locations found.</p>
              ) : null}
              {locationSearchOptions.length > 0 ? (
                <div className="max-h-36 overflow-y-auto rounded-xl border border-border/60 bg-background">
                  {locationSearchOptions.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className="w-full border-b border-border/50 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted/40"
onClick={async () => {
  try {
    if (!mapsApiKey) {
      return
    }

    setOptions({
      key: mapsApiKey,
      v: 'weekly',
    })

    await importLibrary('places')

    const service =
      new google.maps.places.PlacesService(
        document.createElement('div')
      )

 service.getDetails(
  {
    placeId: option.placeId,
    fields: [
      'formatted_address',
      'address_components',
      'geometry',
    ],
  },
  (place, status) => {
    if (
      status !== google.maps.places.PlacesServiceStatus.OK ||
      !place
    ) {
      return
    }

    const getAddressPart = (type: string) =>
      place.address_components?.find((x) =>
        x.types.includes(type)
      )?.long_name || ''

    const countryName = getAddressPart('country')
    const stateName = getAddressPart(
      'administrative_area_level_1'
    )

    // find matching country id
    const selectedCountry =
      countriesQuery.data?.find(
        (country) =>
          country.country_name
            ?.toLowerCase()
            .trim() ===
          countryName.toLowerCase().trim()
      )

    // fetch state id based on selected country
    const selectedState =
      statesQuery.data?.find(
        (state) =>
          state.name
            ?.toLowerCase()
            .trim() ===
          stateName.toLowerCase().trim()
      )

    setAddressForm((current) => ({
      ...current,

      // set addressFor
      addressFor:
        place.formatted_address || '',

      doorNo:
        getAddressPart('street_number'),

      building:
        getAddressPart('premise'),

      street:
        getAddressPart('route'),

      cityName:
        getAddressPart('locality') ||
        getAddressPart(
          'administrative_area_level_2'
        ),

      postcode:
        getAddressPart('postal_code'),

      // set dropdown ids
      country: selectedCountry
        ? String(selectedCountry.id)
        : '',

      state: selectedState
        ? String(selectedState.id)
        : '',

      latitude: String(
        place.geometry?.location?.lat() ?? ''
      ),

      longitude: String(
        place.geometry?.location?.lng() ?? '',
      ),
    }))

    setLocationSearch(
      place.formatted_address ||
      option.label
    )

    setLocationSearchOptions([])
  }
)
  } catch (error) {
    console.error(error)
  }
}}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
              </div>
            </div>

            <div className="rounded-2xl border border-border/70 bg-muted/15 p-2.5">
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Identity</p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <div className="grid gap-2 sm:col-span-2">
              <Label htmlFor="address_for">Address For *</Label>
              <Input
                id="address_for"
                value={toInputValue(addressForm.addressFor)}
                placeholder="e.g. Delivery Address, Branch Address"
                onChange={(event) => setAddressForm((current) => ({ ...current, addressFor: event.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="address_fname">First Name</Label>
              <Input id="address_fname" value={toInputValue(addressForm.fname)} onChange={(event) => setAddressForm((current) => ({ ...current, fname: event.target.value }))} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="address_lname">Last Name</Label>
              <Input id="address_lname" value={toInputValue(addressForm.lname)} onChange={(event) => setAddressForm((current) => ({ ...current, lname: event.target.value }))} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="address_door_no">Door No</Label>
              <Input id="address_door_no" value={toInputValue(addressForm.doorNo)} onChange={(event) => setAddressForm((current) => ({ ...current, doorNo: event.target.value }))} />
            </div>
              </div>
            </div>

            <div className="rounded-2xl border border-border/70 bg-muted/15 p-2.5">
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Location</p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="address_building">Building</Label>
              <Input id="address_building" value={toInputValue(addressForm.building)} onChange={(event) => setAddressForm((current) => ({ ...current, building: event.target.value }))} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="address_street">Street</Label>
              <Input id="address_street" value={toInputValue(addressForm.street)} onChange={(event) => setAddressForm((current) => ({ ...current, street: event.target.value }))} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="address_postcode">Postcode</Label>
              <Input id="address_postcode" value={toInputValue(addressForm.postcode)} onChange={(event) => setAddressForm((current) => ({ ...current, postcode: event.target.value }))} />
            </div>
            <div className="grid gap-2">
              <Label>Country</Label>
              <Select value={addressForm.country} onValueChange={(value) => setAddressForm((current) => ({ ...current, country: value, state: '' }))}>
                <SelectTrigger aria-label="Country" className="w-full">
                  <SelectValue placeholder="Choose country" />
                </SelectTrigger>
                <SelectContent>
                  {(countriesQuery.data ?? []).map((country) => (
                    <SelectItem key={String(country.id)} value={String(country.id)}>
                      {country.country_name || `Country ${country.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>State / Province</Label>
              <Select value={addressForm.state} onValueChange={(value) => setAddressForm((current) => ({ ...current, state: value }))} disabled={!addressForm.country}>
                <SelectTrigger aria-label="State / Province" className="w-full">
                  <SelectValue placeholder={addressForm.country ? 'Choose state' : 'Choose country first'} />
                </SelectTrigger>
                <SelectContent>
                  {(statesQuery.data ?? []).map((state) => (
                    <SelectItem key={String(state.id)} value={String(state.id)}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="address_city_name">City</Label>
              <Input id="address_city_name" value={toInputValue(addressForm.cityName)} onChange={(event) => setAddressForm((current) => ({ ...current, cityName: event.target.value }))} />
            </div>
              </div>
            </div>

            <div className="rounded-2xl border border-border/70 bg-muted/15 p-2.5">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Coordinates</p>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">Optional</Badge>
                  <Badge variant="secondary">
                    {lastResolvedProvider === 'googlemaps'
                      ? `Google Maps (${MAP_PROVIDER_GOOGLE_CODE}) · ${providerSourceLabel}`
                      : `OpenStreetMap (${MAP_PROVIDER_OPENSTREETMAP_CODE}) · ${providerSourceLabel}`}
                  </Badge>
                </div>
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="address_latitude">Latitude</Label>
              <Input id="address_latitude" value={toInputValue(addressForm.latitude)} onChange={(event) => setAddressForm((current) => ({ ...current, latitude: event.target.value }))} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="address_longitude">Longitude</Label>
              <Input id="address_longitude" value={toInputValue(addressForm.longitude)} onChange={(event) => setAddressForm((current) => ({ ...current, longitude: event.target.value }))} />
            </div>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t px-5 py-2">
            <Button variant="outline" onClick={resetDialog} disabled={saveMutation.isPending}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving...' : editingAddress ? 'Update Address' : 'Save Address'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}